import { useMutation } from '@apollo/client';
import { Button } from '@bphxd/ds-core-react';
import { CircleCheck24, Alert24, Clock24 } from '@bphxd/ds-core-react/lib/icons';
import { TCertificate } from '../../../lib/types';
import {
  CertificateStatusEnum,
  CertificateTypeEnum,
} from '../../../graph-queries/__generated__/graphql';
import {
  GET_ALL_CERTIFICATES_WITH_JOINT_DETAILS,
  UPDATE_CERTIFICATE,
} from '../../../graph-queries/tar/certificate';
import { useHasMaintenanceAdminAccess } from '../../../lib/util/auth';
import ConfirmationModal from '../../molecules/ConfirmationModal/ConfirmationModal';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';
import { all } from 'axios';

const SECONDS_IN_A_MINUTE = 60;
const MINUTES_IN_AN_HOUR = 60;
const SECONDS_IN_AN_HOUR = MINUTES_IN_AN_HOUR * SECONDS_IN_A_MINUTE;
const JOINT_TIGHTENED_STEP_NO = 3;
const JOINT_WITNESSED_STEP_NO = 4;
const START_UP_STEP_NO = 6;
const NOTICE_BAR_TIMEOUT = 3000;

/**
 * Formats an elapsed time (in seconds) as "X hr Y min".
 */
const formatElapsedTime = (diffSeconds: number): string => {
  const hours = Math.floor(diffSeconds / SECONDS_IN_AN_HOUR);
  const minutes = Math.floor((diffSeconds % SECONDS_IN_AN_HOUR) / SECONDS_IN_A_MINUTE);
  return `${hours} hr ${minutes} min`;
};

/**
 * Builds a tooltip for non time-based steps (steps 1–6) by listing non-compliant joints.
 */
const buildNonTimeBasedTooltip = (
  certificate: TCertificate,
  stepTitle: string,
  stepNo: number
): string => {
  const nonCompliantDetails =
    certificate.tarJoints
      ?.map((joint) => {
        const step = joint.tarJointInstallationSteps?.find((s) => s.stepNo === stepNo);
        if (!step || !step.signedBy || !step.signedBy.trim()) {
          return `Joint ${joint.jointNumber} (Tag: ${joint.tagId}) - ${joint.additionalDetails}`;
        }
        return null;
      })
      .filter((detail) => detail) || [];

  return nonCompliantDetails.length > 0
    ? `${stepTitle} incomplete.\n\nIncomplete joints:\n${nonCompliantDetails.join('\n')}`
    : `${stepTitle} incomplete`;
};

/**
 * Returns an icon for non time-based steps (steps 1–6).
 */
const getNonTimeBasedStatusIcon = (
  certificate: TCertificate,
  stepNo: number,
  stepTitle: string
) => {
  if (certificate.type === CertificateTypeEnum.Sbt && stepNo === JOINT_TIGHTENED_STEP_NO) {
    return <>N/A</>;
  }
  if (stepNo === JOINT_WITNESSED_STEP_NO) return getWitnessStepIcon(certificate);

  const allStepInstances =
    certificate.tarJoints?.flatMap(
      (joint) => joint.tarJointInstallationSteps?.filter((step) => step.stepNo === stepNo) || []
    ) || [];

  if (allStepInstances.length === 0) return null;

  const allSigned = allStepInstances.every(
    (step) => step.signedBy && step.signedBy.trim().length > 0
  );
  const tooltip = buildNonTimeBasedTooltip(certificate, stepTitle, stepNo);
  return allSigned ? (
    <CircleCheck24 title={`${stepTitle} complete for all joints`} color="#15AD5C" />
  ) : (
    <Clock24 title={tooltip} />
  );
};

/**
 * Returns an icon for the witness step since it is unique in that not every joint has step required
 */
const getWitnessStepIcon = (certificate: TCertificate) => {
  if (certificate.tarJoints?.every((joint) => joint.supervisedAssembly === false)) {
    return <>N/A</>;
  }
  const jointsWithWitness =
    certificate.tarJoints?.filter((joint) => joint.supervisedAssembly === true) || [];
  const allStepInstances =
    jointsWithWitness?.flatMap(
      (joint) =>
        joint.tarJointInstallationSteps?.filter(
          (step) => step.stepNo === JOINT_WITNESSED_STEP_NO
        ) || []
    ) || [];
  const allSigned = allStepInstances.every(
    (step) => step.signedBy && step.signedBy.trim().length > 0
  );
  const nonCompliantDetails =
    jointsWithWitness
      ?.map((joint) => {
        const step = joint.tarJointInstallationSteps?.find(
          (s) => s.stepNo === JOINT_WITNESSED_STEP_NO
        );
        if (!step || !step.signedBy || !step.signedBy.trim()) {
          return `Joint ${joint.jointNumber} (Tag: ${joint.tagId}) - ${joint.additionalDetails}`;
        }
        return null;
      })
      .filter((detail) => detail) || [];

  const tooltip =
    nonCompliantDetails.length > 0
      ? `Witness incomplete.\n\nIncomplete joints:\n${nonCompliantDetails.join('\n')}`
      : `Witness incomplete`;
  return allSigned ? (
    <CircleCheck24 title={`Witness complete for all joints`} color="#15AD5C" />
  ) : (
    <Clock24 title={tooltip} />
  );
};

export const isStepCompleteForAllJoints = (certificate: TCertificate, stepNo: number) => {
  let allTargetSteps =
    certificate.tarJoints?.flatMap(
      (joint) => joint.tarJointInstallationSteps?.filter((step) => step.stepNo === stepNo) || []
    ) || [];

  if (stepNo === 4) {
    allTargetSteps =
      certificate.tarJoints?.flatMap(
        (joint) => joint.tarJointInstallationSteps?.filter((step) => step.stepNo === stepNo && joint.supervisedAssembly) || []
      ) || [];
  }


  if (allTargetSteps.length === 0) return null;

  const allSigned = allTargetSteps.every(
    (step) => step.signedBy && step.signedBy.trim().length > 0
  );
  return allSigned;
};

export const isAllComplete = (certificate: TCertificate) => {
  if (!certificate || !certificate.tarJoints?.length) {
    return false;
  }
  const allComplete = certificate.tarJoints?.every((joint) => {
    const allSteps = joint.tarJointInstallationSteps || [];

    const isSbt = certificate.type === CertificateTypeEnum.Sbt;
    const isSupervised = joint.supervisedAssembly !== false;

    const excludedSteps = [
      ...(isSbt ? [JOINT_TIGHTENED_STEP_NO] : []),
      ...(!isSupervised ? [JOINT_WITNESSED_STEP_NO] : []),
    ];

    const stepsToCheck = allSteps.filter((step) => !excludedSteps.includes(step.stepNo));

    return stepsToCheck.every((step) => step.signedBy && step.signedBy.trim().length > 0);
  });
  return allComplete;
};

/**
 * Returns an icon for time-based steps (steps 7–10) along with a tooltip.
 */
const getTimeBasedStatusIcon = (certificate: TCertificate, stepNo: number, stepTitle: string) => {
  const allSigned = isStepCompleteForAllJoints(certificate, stepNo);
  if (allSigned === null) return null;

  if (allSigned) {
    return <CircleCheck24 title={`${stepTitle} complete for all joints`} color="#15AD5C" />;
  }

  // Define thresholds (in seconds) for time-based steps 7–10.
  const thresholdMapping: Record<number, number> = {
    7: 12 * SECONDS_IN_AN_HOUR,
    8: 24 * SECONDS_IN_AN_HOUR,
    9: 36 * SECONDS_IN_AN_HOUR,
    10: 48 * SECONDS_IN_AN_HOUR,
  };
  const threshold = thresholdMapping[stepNo] || 0;

  // Process each joint: if its target step is incomplete, calculate its own elapsed time from the Start Up step.
  const overdueJointDetails =
    certificate.tarJoints
      ?.map((joint) => {
        const targetStep = joint.tarJointInstallationSteps?.find((s) => s.stepNo === stepNo);
        if (targetStep && targetStep.signedBy && targetStep.signedBy.trim().length > 0) {
          // Already signed; skip this joint.
          return null;
        }
        const startStep = joint.tarJointInstallationSteps?.find(
          (s) => s.stepNo === START_UP_STEP_NO && s.signedAt
        );
        if (startStep && startStep.signedAt) {
          const elapsedSeconds =
            (new Date().getTime() - new Date(startStep.signedAt).getTime()) / 1000;
          // Only include this joint if its elapsed time exceeds the threshold.
          if (elapsedSeconds >= threshold) {
            return `Joint ${joint.jointNumber} (Tag: ${joint.tagId}) - ${joint.additionalDetails} [Elapsed: ${formatElapsedTime(elapsedSeconds)}]`;
          }
        }
        return null;
      })
      .filter((detail) => detail) || [];

  // Build a tooltip: only list joints that are overdue.
  const tooltip =
    overdueJointDetails.length > 0
      ? `${stepTitle} incomplete.\n\nOverdue joints:\n${overdueJointDetails.join('\n')}`
      : `${stepTitle} incomplete (no joints overdue)`;

  if (overdueJointDetails.length > 0) {
    return <Alert24 title={tooltip} color="#E64949" />;
  }

  // If none of the joints are individually overdue, return null
  return null;
};

/**
 * Main helper function to return an appropriate icon for the given step.
 */
export const getStepStatusIcon = (certificate: TCertificate, stepNo: number, stepTitle: string) => {
  return stepNo < 7
    ? getNonTimeBasedStatusIcon(certificate, stepNo, stepTitle)
    : getTimeBasedStatusIcon(certificate, stepNo, stepTitle);
};

export const ActionColumn: React.FC<{ certificate: TCertificate }> = ({ certificate }) => {
  const hasMaintenanceAdminAccess = useHasMaintenanceAdminAccess();
  const { sendSuccess, sendError } = useNoticeBar();
  const [updateCertificate] = useMutation(UPDATE_CERTIFICATE, {
    refetchQueries: [
      {
        query: GET_ALL_CERTIFICATES_WITH_JOINT_DETAILS,
        variables: { assetId: certificate.assetId },
      },
    ],
    awaitRefetchQueries: true,
  });

  const allComplete = isAllComplete(certificate);

  const handleStatusChange = async () => {
    try {
      updateCertificate({
        variables: {
          input: {
            certificateId: certificate.certificateId,
            assetId: certificate.assetId,
            title: certificate.title,
            type: certificate.type,
            certificateNumber: certificate.certificateNumber,
            status: CertificateStatusEnum.Complete,
            template: certificate.template,
          },
        },
      }).then(() => {
        sendSuccess(`Certificate ${certificate.certificateId} was completed`, NOTICE_BAR_TIMEOUT);
      });
    } catch (error) {
      console.error('Error marking certificate as complete:', error);
      sendError(
        `Error marking certificate ${certificate.certificateId} as complete`,
        NOTICE_BAR_TIMEOUT
      );
    }
  };

  return (
    <ConfirmationModal
      title="Change Certificate Status"
      message={`Are you sure you want to change status from Approved to Complete?`}
      confirmButtonText="Yes, change status"
      cancelButtonText="Cancel"
      onConfirm={handleStatusChange}
      renderButton={(openModal) => (
        <Button
          style={{
            cursor: !hasMaintenanceAdminAccess ? 'not-allowed' : 'pointer',
            display: 'inline-block',
          }}
          disabled={!(hasMaintenanceAdminAccess && allComplete)}
          size="xs"
          level="primary"
          onClick={openModal}
          title={
            !hasMaintenanceAdminAccess
              ? "You don't have permission to perform this action"
              : !allComplete
                ? 'All joint steps have not been signed'
                : 'Set status to Complete'
          }
        >
          Complete
        </Button>
      )}
    />
  );
};
